/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {utils} from 'phenix-web-sdk';

const qualities = Object.freeze([
  'uld',
  'vvld',
  'vld',
  'ld',
  'sd',
  'hd',
  'fhd',
  'xhd',
  'uhd'
]);
const config = {
  express: {baseUri: getBaseUrl()},
  selectionStrategy: getSelectionStrategy(),
  useShaka: useShaka(),
  audio: {
    muted: isAudioMuted(),
    disableAutoMuting: isAutoMutingDisabled()
  },
  video: {align: getVideoAlighment()},
  mobileAppInstallationLink: getMobileAppInstallationLink(),
  enableExperimentalFeatures: areExperimentalFeaturesEnabled(),
  publisher: {
    capabilities: getPublisherCapabilities(),
    token: getPublishToken(),
    decodedToken: decodeToken()
  },
  subscriber: {
    targetLatency: getTargetLatency(),
    hlsTargetDuration: getHlsTargetDuration(),
    token: getStreamToken()
  },
  cdn: {
    pattern: getStreamingSourceMappingPattern(),
    replacement: getStreamingSourceMappingReplacement()
  },
  channel: {
    primaryAlias: getChannelAlias(),
    switchDelay: getChannelSwitchDelay()
  },
  environment: {
    isMobile: isMobile(),
    isSafari: isSafari(),
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    canPlayHls: canPlayHls()
  },
  auth: {token: getAuthToken()},
  offlinePage: {
    imageUrl: getOfflineImageUrl(),
    text: getOfflineText(),
    disableText: disableOfflineText()
  },
  qualities: qualities
};

function getPublisherCapabilities() {
  let capabilities = ['multi-bitrate'];
  const additionalCapabilities = getAdditionalPublisherCapabilities();

  if (getStreamQuality()) {
    capabilities.push(getStreamQuality());
  }

  if (additionalCapabilities.length > 0) {
    capabilities = capabilities
      .concat(additionalCapabilities)
      .filter(removeDuplicates);
  }

  if (isMbrDisabled()) {
    capabilities = capabilities.filter(capability => capability !== 'multi-bitrate');
  }

  const publisherQualities = capabilities.reduce((foundQualities, capability) => {
    if (qualities.includes(capability)) {
      foundQualities.push(capability);
    }

    return foundQualities;
  }, []);

  publisherQualities.pop();
  publisherQualities.forEach((quality) => {
    capabilities = capabilities.filter(capability => capability !== quality);
  });

  return capabilities;
}

function getBaseUrl() {
  const defaultBaseUrl = getDefaultBaseUrl();

  return resolveUrl(getUrlParameter('uri', defaultBaseUrl, defaultBaseUrl), true);
}

function getDefaultBaseUrl() {
  if (utils.rtc.browser === 'IE') {
    return window.location.protocol + '//' + window.location.host;
  }

  return window.location.origin;
}

function getTargetLatency() {
  return getUrlParameter('targetLatency') ? parseFloat(getUrlParameter('targetLatency')) : null;
}

function getStreamQuality() {
  return getUrlParameter('quality') || getUrlParameter('q');
}

function getSelectionStrategy() {
  return mapQueryParameterSelectionStrategy(getUrlParameter('selectionStrategy') || getUrlParameter('ss'));
}

function useShaka() {
  return getUrlParameter('shaka', true);
}

function decodeToken() {
  try {
    const token = getPublishToken();

    if (!token) {
      return;
    }

    if (!token.startsWith('DIGEST:')) {
      console.error('Invalid Token');

      return;
    }

    const decodedTokenString = atob(token.replace('DIGEST:', ''));
    let decodedToken = JSON.parse(decodedTokenString);

    if (decodedToken.token && typeof(decodedToken.token) === 'string') {
      decodedToken = {
        ...decodedToken,
        ...JSON.parse(decodedToken.token)
      };
    }

    return decodedToken;
  } catch (e) {
    console.error('Invalid Token', e);

    return;
  }
}

function getAdditionalPublisherCapabilities() {
  const capabilitiesString = getUrlParameter('capabilities') || getUrlParameter('c') || '';
  const tokenCapabilities = decodeToken()?.capabilities;

  if (!capabilitiesString && !tokenCapabilities) {
    return [];
  }

  const queryParamCapabilities = capabilitiesString.split(',');
  let capabilities = queryParamCapabilities
    .concat(tokenCapabilities)
    .filter(removeDuplicates);

  if (getUrlParameter('disableDrm')) {
    capabilities = capabilities.filter((item) => {
      return item !== 'drm';
    });
  }

  return capabilities;
}

function getStreamingSourceMappingPattern() {
  return getUrlParameter('cdnPattern');
}

function isAudioMuted() {
  let mute = getUrlParameter('mute', 'true');

  if (mute === 'false') {
    return false;
  } else if (mute === 'true') {
    return true;
  }
}

function isAutoMutingDisabled() {
  return getUrlParameter('disableAutoMute', 'true') === 'true';
}

function getVideoAlighment() {
  let align = getUrlParameter('align');

  if (align) {
    return align;
  }

  return 'center';
}

function getStreamingSourceMappingReplacement() {
  if (getUrlParameter('cdnHls') && (isIOS() || isSafari())) {
    return getUrlParameter('cdnHls');
  }

  if (getUrlParameter('cdnDash') && !(isIOS() || isSafari())) {
    return getUrlParameter('cdnDash');
  }

  return getUrlParameter('cdn');
}

function getMobileAppInstallationLink() {
  if (isAndroid()) {
    return getUrlParameter('android');
  }

  if (isIOS()) {
    return getUrlParameter('ios');
  }

  return null;
}

function getHlsTargetDuration() {
  const hlsTargetDuration = getUrlParameter('hlsTargetDuration');

  if (hlsTargetDuration) {
    return parseInt(hlsTargetDuration, 10);
  }

  return null;
}

function getAuthToken() {
  const authToken = getUrlParameter('authToken') || getUrlParameter('edgeToken') || getUrlParameter('token');

  if (!authToken) {
    return authToken;
  }

  return decodeURIComponent(authToken);
}

function getPublishToken() {
  const publishToken = getUrlParameter('publishToken');

  if (!publishToken) {
    return publishToken;
  }

  return decodeURIComponent(publishToken);
}

function getStreamToken() {
  const streamToken = getUrlParameter('streamToken') || getUrlParameter('edgeToken') || getUrlParameter('token');

  if (!streamToken) {
    return streamToken;
  }

  return decodeURIComponent(streamToken);
}

function getOfflineImageUrl() {
  return getUrlParameter('offlineImage', '');
}

function getOfflineText() {
  return getUrlParameter('offlineText', '');
}

function disableOfflineText() {
  return getUrlParameter('disableOfflineText', 'false') === 'true';
}

function getChannelAlias() {
  return decodeURIComponent(window.location.hash.substring(1)) || 'default';
}

function getChannelSwitchDelay() {
  return isMobile() ? 1000 : 500;
}

function isMbrDisabled() {
  return getUrlParameter('disableMbr', 'true', 'false') === 'true';
}

function areExperimentalFeaturesEnabled() {
  return getUrlParameter('experimental', 'true', 'false') === 'true';
}

// TODO deprecated
function mapQueryParameterSelectionStrategy(strategy) {
  switch (strategy) {
    case 'ha':
      return 'high-availability';
    case 'mr':
      return 'most-recent';
    default:
      return strategy;
  }
}

function getUrlParameter(parameterName, defaultValue, undefinedValue) {
  const queryParameters = window.location.search.substring(1).split('&');
  const queryParameter = queryParameters.find((_queryParameter) => {
    const equalsIndex = _queryParameter.indexOf('=');
    const parameter = _queryParameter.substring(0, equalsIndex);

    return parameter === parameterName || queryParameter === parameterName;
  });

  if (!queryParameter) {
    return undefinedValue;
  }

  if (!queryParameter.includes('=')){
    return defaultValue;
  }

  return decodeURIComponent(queryParameter.substring(queryParameter.indexOf('=') + 1, queryParameter.length));
}

function resolveUrl(url, removeTrailingSlash) {
  if (!url) {
    return '';
  }

  const resolver = document.createElement('a');

  resolver.href = url;

  let resolvedUrl = resolver.href;

  if (removeTrailingSlash && resolvedUrl && resolvedUrl[resolvedUrl.length - 1] === '/') {
    return resolvedUrl.substring(0, resolvedUrl.length - 1);
  }

  return resolvedUrl;
}

function isMobile() {
  const userAgent = window.navigator.userAgent;

  return isIOS() || /Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent);
}

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function isAndroid() {
  const ua = navigator.userAgent.toLowerCase();

  return ua.includes('android');
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function canPlayHls() {
  return document.createElement('video').canPlayType('application/vnd.apple.mpegURL') === 'maybe';
}

function removeDuplicates(item, index, allItems) {
  return index === allItems.indexOf(item) && item;
}

export default config;